import React from "react"
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	Typography,
} from "@mui/material"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import {format} from "date-fns"

import type {ActiveCase} from "@indebted/api"
import {colors} from "@indebted/theme/colors"

const CLIENT_WAIT_PARTY = "CLIENT"

type RaiseJiraTicketDialogProps = {
	open: boolean
	activeCase: ActiveCase
	clientName?: string
	customerName?: string
	customerRef?: string
	raising: boolean
	onClose: () => void
	onConfirm: () => void
}

function RaiseJiraTicketDialog({
	open,
	activeCase,
	clientName,
	customerName,
	customerRef,
	raising,
	onClose,
	onConfirm,
}: RaiseJiraTicketDialogProps) {
	const escalation = clientEscalation(activeCase)
	const queue = escalation?.Queue
	const accountRefs = activeCase.Accounts.map((account) => account.AccountRef).filter(Boolean)
	const dueDate = formatDueDate(activeCase.ExpiryDate)
	const blockedReason = escalation && !escalation.OpensTicket ? escalation.Reason || defaultBlockedReason : null

	return (
		<Dialog open={open} onClose={onClose} onClick={(e) => e.stopPropagation()} maxWidth="sm" fullWidth>
			<DialogContent>
				<Box textAlign="center" mb={3} mt={1}>
					<Typography variant="h6" fontWeight={600} color={colors.endlessGalaxy["800"]} mb={0.5}>
						{queue ? `Raise Ticket on ${queue}` : "Raise a client support ticket"}
					</Typography>
					<Chip label={activeCase.CaseType} size="small" variant="outlined" sx={{mt: 1}} />
				</Box>

				<Box
					display="flex"
					gap={1.5}
					p={2}
					mb={2}
					bgcolor={colors.gray["50"]}
					border={`1px solid ${colors.gray["200"]}`}
					borderRadius={2}
					color={colors.gray["700"]}
				>
					<InfoOutlinedIcon fontSize="small" sx={{color: colors.gray["500"], mt: 0.25}} />
					<Box flex={1}>
						<Typography variant="subtitle1" fontWeight={600} color="inherit">
							What the ticket carries
						</Typography>
						<Typography variant="body2" color={colors.gray["600"]}>
							Built from the case and its submission answers.
						</Typography>
					</Box>
				</Box>

				{clientName && (
					<TicketFact label="Client">
						<Typography variant="body2">{clientName}</Typography>
					</TicketFact>
				)}

				{(customerName || customerRef) && (
					<TicketFact label="Customer">
						{customerName && <Typography variant="body2">{customerName}</Typography>}
						{customerRef && (
							<Typography variant="caption" color="textSecondary">
								{customerRef}
							</Typography>
						)}
					</TicketFact>
				)}

				<TicketFact label="Accounts">
					{accountRefs.length > 0 ? (
						<Box display="flex" flexWrap="wrap" gap={0.5} justifyContent="flex-end">
							{accountRefs.map((ref) => (
								<Chip
									key={ref}
									label={ref}
									title={ref}
									size="small"
									variant="outlined"
									sx={{maxWidth: 180}}
								/>
							))}
						</Box>
					) : (
						<Typography variant="body2" color="textSecondary">
							None on this case
						</Typography>
					)}
				</TicketFact>

				{dueDate && (
					<TicketFact label="Due date">
						<Typography variant="body2">{dueDate}</Typography>
					</TicketFact>
				)}

				{blockedReason && (
					<Alert severity="warning" sx={{mt: 2}}>
						{blockedReason}
					</Alert>
				)}
			</DialogContent>
			<DialogActions sx={{px: 3, pb: 3, pt: 2}}>
				<Button
					onClick={onClose}
					variant="outlined"
					disabled={raising}
					sx={{flex: 1, borderRadius: 2, color: colors.warmBlue["900"], borderColor: colors.warmBlue["900"]}}
				>
					CANCEL
				</Button>
				<Button
					onClick={onConfirm}
					variant="contained"
					disabled={raising}
					startIcon={raising ? <CircularProgress size={16} color="inherit" /> : undefined}
					sx={{
						flex: 1,
						borderRadius: 2,
						backgroundColor: colors.warmBlue["900"],
						"&:hover": {backgroundColor: colors.warmBlue["800"]},
					}}
				>
					RAISE TICKET
				</Button>
			</DialogActions>
		</Dialog>
	)
}

function TicketFact({label, children}: {label: string; children: React.ReactNode}) {
	return (
		<Box
			display="flex"
			alignItems="flex-start"
			justifyContent="space-between"
			gap={2}
			py={1.25}
			borderTop={`1px solid ${colors.gray["200"]}`}
		>
			<Typography variant="body2" color="textSecondary">
				{label}
			</Typography>
			<Box textAlign="right" display="flex" flexDirection="column" alignItems="flex-end">
				{children}
			</Box>
		</Box>
	)
}

const defaultBlockedReason = "This case can open no client support ticket."

function clientEscalation(activeCase: ActiveCase) {
	return activeCase.WaitParties.find((party) => party.ID === CLIENT_WAIT_PARTY)?.Escalation ?? null
}

function formatDueDate(expiryDate: string): string | null {
	if (!expiryDate) return null
	const parsed = new Date(expiryDate)
	if (Number.isNaN(parsed.getTime())) return null
	return format(parsed, "d MMM yyyy")
}

export {RaiseJiraTicketDialog}
